// @ts-strict-ignore
import * as React from 'react';

import { NeedlAutocompletePopper } from '@/components/NeedlAutocomplete';
import type { Option } from '@/components/NoteSelector/types';
import { isCreatable, isNotebook } from '@/components/NoteSelector/utils';
import { useCreateNotebook } from '@/features/Notes/hooks/useCreateNotebook';
import { useFeedNotebooks } from '@/features/Notes/hooks/useFeedNotebooks';
import type { Notebook } from '@/features/Notes/types';
import { useBoard } from '@/hooks/boards-hooks';
import { useCurrentChannel } from '@/hooks/query/useCurrentChannel';
import { useGetSearchId } from '@/hooks/useGetSearchId';
import { sortSelectedSourcesFromTwoObjectes } from '@/utils/sort';

const NoteSelectorContext = React.createContext<NoteSelectorContextType | null>(
  null
);

export const useNoteSelectorContext = () => {
  const context = React.useContext(NoteSelectorContext);

  if (!context) {
    throw new Error(
      'useNoteSelectorContext must be used within a NoteSelectorProvider'
    );
  }

  return context;
};

export const NoteSelectorProvider: React.FunctionComponent<
  NoteSelectorProviderType
> = ({
  children,
  disabled: userDisabled,
  onSuccess,
  onClose,
  onOpen,
  singleSelect,
}) => {
  const [openState, setOpenState] = React.useState<Notebook[]>([]);
  const [disableAutocomplete, setDisableAutocomplete] = React.useState(false);

  const selectionRef = React.useRef<string | null>(null);

  const { board_id: feedId, channel_id: channelId } = useGetSearchId();
  const { board_name } = useBoard();
  const feedName =
    feedId === 'home' || board_name === 'home'
      ? 'Home' // default notebook name should be something different than home
      : board_name;
  const { name: channelName } = useCurrentChannel();

  const { isLoading: feedNotebooksLoading, data: notebooks } =
    useFeedNotebooks();

  const createNotebookMutation = useCreateNotebook();

  const { anchorEl, handleClose, handleOpen, open } =
    NeedlAutocompletePopper.usePopperState();

  const isNameLoading = !(feedName || channelName);

  const openSelector = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onOpen?.(event);
      handleOpen(event);
    },
    [handleOpen, onOpen]
  );

  const closeSelector = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClose?.(event);
      handleClose(event, 'blur');
      setOpenState([]);
    },
    [handleClose, onClose]
  );

  const defaultNotebookIndex = React.useMemo(
    () => notebooks?.findIndex((notebook) => notebook.default === true) || 0,
    [notebooks]
  );

  const id = open ? 'note-label' : undefined;

  const notebooksSet = React.useMemo(
    () => new Set(notebooks?.map(({ notebook_name }) => notebook_name) ?? []),
    [notebooks]
  );

  const options = React.useMemo(() => {
    const name = channelName || feedName;
    return [
      ...(defaultNotebookIndex >= 0
        ? []
        : [
            {
              inputValue: name,
              title: (
                <>
                  Create Default Notebook <b>&quot;{name}&quot;</b>
                </>
              ),
              type: 'default',
            },
          ]),
      ...sortSelectedSourcesFromTwoObjectes(
        notebooks ?? [],
        openState ?? [],
        'notebook_id'
      ),
    ];
  }, [channelName, defaultNotebookIndex, feedName, notebooks, openState]);

  const primaryButtonHandler = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const notebooks = [...(openState || [])];
      if (notebooks.length === 0) {
        return;
      }

      onSuccess?.(notebooks);
      closeSelector(event);
    },
    [closeSelector, onSuccess, openState]
  );

  const onChange = React.useCallback(
    (e: React.ChangeEvent<unknown>, values: Option[]) => {
      const newNotebook = values.find(isCreatable);
      if (newNotebook) {
        setDisableAutocomplete(true);
        createNotebookMutation.mutate(
          {
            name: newNotebook.inputValue,
            feedId,
            channelId,
            type: newNotebook.type,
          },
          {
            onSettled: (data) => {
              setDisableAutocomplete(false);
              selectionRef.current = data?.data?.results?.notebookId ?? null;
            },
          }
        );
        return;
      }
      if (singleSelect) {
        const previouslySelectedNotebookSet = new Set(
          openState.map(({ notebook_id }) => notebook_id)
        );
        const currentlySelectedNotebook = values.filter((value) =>
          typeof value !== 'string' && isNotebook(value)
            ? !previouslySelectedNotebookSet.has(value.notebook_id)
            : false
        );
        setOpenState(currentlySelectedNotebook as Notebook[]);
        return;
      }
      setOpenState(values as Notebook[]);
    },
    [channelId, createNotebookMutation, feedId, openState, singleSelect]
  );

  // it is fine here to update the state on render
  // because we want to update the UI before the next render
  // and we don't want to wait for the next render to update the state in useEffect
  // it is also protected by ref
  if (selectionRef.current !== null && !createNotebookMutation.isLoading) {
    // since we allow for creating a notebook on the fly
    // we should also select the notebook that was just created
    const createdNotebook = notebooks?.find(
      ({ notebook_id }) => notebook_id === selectionRef.current
    );
    selectionRef.current = null;
    if (createdNotebook) {
      if (singleSelect) {
        setOpenState([createdNotebook]);
      } else {
        setOpenState((prev) => [...prev, createdNotebook]);
      }
    }
  }

  const disabled = userDisabled || feedNotebooksLoading || isNameLoading;

  return (
    <NoteSelectorContext.Provider
      value={React.useMemo(
        () => ({
          disabled,
          openSelector,
          id,
          open,
          anchorEl,
          disableAutocomplete,
          options,
          closeSelector,
          openState,
          onChange,
          primaryButtonHandler,
          notebooksSet,
          singleSelect,
        }),
        [
          anchorEl,
          closeSelector,
          disableAutocomplete,
          disabled,
          id,
          notebooksSet,
          onChange,
          open,
          openSelector,
          openState,
          options,
          primaryButtonHandler,
          singleSelect,
        ]
      )}
    >
      {children}
    </NoteSelectorContext.Provider>
  );
};

type NoteSelectorProviderType = {
  children: React.ReactNode;
  disabled?: boolean;
  onClose?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onOpen?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSuccess?: (notebooks: Notebook[]) => void;
  singleSelect?: boolean;
};

type NoteSelectorContextType = {
  disabled: boolean;
  openSelector: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  id: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  disableAutocomplete: boolean;
  options: Option[];
  closeSelector: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  openState: Notebook[];
  onChange: (e: React.ChangeEvent<unknown>, value: (Option | string)[]) => void;
  primaryButtonHandler: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  notebooksSet: Set<string>;
  singleSelect: boolean;
};
